/**
 * <s-club-offer> — Mira Girls Club envelope pop-up
 * Opens its <dialog> once for first-time visitors after a short delay, lets the
 * visitor "open" the envelope to reveal the 10% offer and sign-up form, and
 * reopens on the revealed state after the sign-up form posts back.
 *
 * Attributes:
 *   data-delay       — milliseconds before the first-visit pop-up opens (default 4000)
 *   data-storage-key — localStorage key remembering that the pop-up was seen
 *   data-form-id     — id of the sign-up form; its URL hash marks a post-back
 *   data-posted      — "true" when the server rendered a success or error state
 */
class SClubOffer extends HTMLElement {
  #dialog;
  #ac;
  #timer = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const storageKey = this.dataset.storageKey || 'mira-club-offer-seen';
    const formHash = `#${this.dataset.formId || ''}`;

    this.querySelector('[data-club-envelope]')?.addEventListener('click', () => this.#reveal(), opts);

    // Backdrop clicks land on the dialog element itself.
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#dialog.close();
    }, opts);

    this.#dialog.addEventListener('close', () => {
      this.#remember(storageKey);
    }, opts);

    if (this.dataset.posted === 'true' && window.location.hash === formHash) {
      this.dataset.state = 'open';
      this.#open();
      return;
    }

    if (this.#seen(storageKey)) return;

    const delay = Number.parseInt(this.dataset.delay || '4000', 10);
    this.#timer = window.setTimeout(() => this.#open(), Number.isNaN(delay) ? 4000 : delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    if (this.#timer) window.clearTimeout(this.#timer);
  }

  #open() {
    if (this.#dialog.open) return;
    // Another modal (cart, menu) already owns the screen; wait for the next visit.
    if (document.querySelector('dialog[open]:modal')) return;
    this.#dialog.showModal();
  }

  #reveal() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finish = () => {
      this.dataset.state = 'open';
      this.querySelector('[data-club-email]')?.focus();
    };

    if (reduceMotion) {
      finish();
      return;
    }

    this.dataset.state = 'opening';
    window.setTimeout(finish, 320);
  }

  #seen(key) {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }

  #remember(key) {
    try {
      window.localStorage.setItem(key, '1');
    } catch {
      // Storage can be unavailable in private modes; the pop-up then simply shows again.
    }
  }
}

if (!customElements.get('s-club-offer')) {
  customElements.define('s-club-offer', SClubOffer);
}
